import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://images.unsplash.com/photo-1758272421829-7028296dee8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxoZWFsdGh5JTIwYWN0aXZlJTIwbGlmZXN0eWxlJTIwY2FsbXxlbnwwfHx8fDE3NzYxMDA2OTR8MA&ixlib=rb-4.1.0&q=85"; // TODO: replace with custom photography per brand kit
const BACKDROP_IMG = "https://images.unsplash.com/photo-1601448971494-0cb12d3bbb53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxzbW9vdGglMjBzYW5kJTIwdGV4dHVyZSUyMHdhcm18ZW58MHx8fHwxNzc2MTAwNzA2fDA&ixlib=rb-4.1.0&q=85"; // TODO: replace with custom photography per brand kit

export default function HeroSection() {
  // Phase 2: live-queried hero metrics. No hardcoded marketing numbers.
  const [stats, setStats] = useState({ peptide_count: null, states_licensed_count: null });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/public/site-stats`)
      .then((r) => {
        if (!cancelled) setStats(r.data || {});
      })
      .catch(() => {
        // Silent fall-through — if the API fails, we hide the metrics rather than showing fake ones.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToQuiz = () => {
    const el = document.querySelector("#quiz");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden"
    >
      {/* Mobile-first: hero image becomes full-width background with overlay
          on small screens. Desktop keeps the side-by-side layout. */}
      <div
        data-testid="hero-mobile-bg"
        className="absolute inset-0 lg:hidden"
        aria-hidden="true"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F7F5F0]/50 via-[#F7F5F0]/85 to-[#F7F5F0]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-6 block">
              Physician-Guided Peptide Therapy
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-[5rem] font-serif font-light tracking-tighter leading-[1.1] text-[#2A3324] mb-6">
              Transform Your Health with{" "}
              <span className="italic font-normal">Peptide Recovery</span>
            </h1>
            <p className="text-base md:text-lg font-sans font-light leading-relaxed text-[#2A3324]/70 max-w-lg mb-10">
              Discover science-backed peptide treatments designed to optimize
              your body's natural processes. From anti-aging to recovery,
              unlock your full potential.
            </p>
            {/* Single CTA — split CTAs reduce conversion. The "Explore Peptides"
                secondary button has been removed in Phase 2. Pricing now lives
                on /pricing and on every peptide card below. */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-quiz-btn"
                onClick={scrollToQuiz}
                className="bg-[#5C715E] text-[#F7F5F0] hover:bg-[#4A5A4B] rounded-full px-8 min-h-14 sm:min-h-12 py-4 text-base md:text-lg font-sans font-medium tracking-wide transition-all duration-300 hover:-translate-y-1 shadow-sm inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                Take the Quiz — Free Doctor Consult
                <ArrowRight size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* Phase 2 — REAL metrics from db query, plus the money-back badge.
                Mobile: horizontal scroll instead of stacking (per Phase 3 spec). */}
            <div data-testid="hero-trust-row" className="mt-12 -mx-6 md:mx-0 px-6 md:px-0 flex items-start gap-6 md:gap-8 overflow-x-auto md:flex-wrap snap-x snap-mandatory scrollbar-hide">
              {stats.peptide_count > 0 && (
                <div data-testid="hero-stat-peptides" className="snap-start shrink-0">
                  <p className="text-3xl font-serif font-semibold text-[#2A3324]">
                    {stats.peptide_count}
                  </p>
                  <p className="text-sm font-sans text-[#2A3324]/50 whitespace-nowrap">Peptides on offer</p>
                </div>
              )}
              {stats.states_licensed_count > 0 && (
                <>
                  <div className="w-px h-10 bg-[#2A3324]/10 hidden sm:block" />
                  <div data-testid="hero-stat-states" className="snap-start shrink-0">
                    <p className="text-3xl font-serif font-semibold text-[#2A3324]">
                      {stats.states_licensed_count}
                    </p>
                    <p className="text-sm font-sans text-[#2A3324]/50 whitespace-nowrap">
                      State{stats.states_licensed_count === 1 ? "" : "s"} licensed
                    </p>
                  </div>
                </>
              )}
              <div className="w-px h-10 bg-[#2A3324]/10 hidden sm:block" />
              <div data-testid="hero-stat-shipping" className="snap-start shrink-0">
                <p className="text-3xl font-serif font-semibold text-[#2A3324]">Free</p>
                <p className="text-sm font-sans text-[#2A3324]/50 whitespace-nowrap">Overnight shipping</p>
              </div>
            </div>

            {/* Money-back guarantee badge */}
            <div data-testid="hero-money-back-badge" className="mt-8 inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-full text-xs md:text-sm font-sans font-medium text-emerald-800">
              <ShieldCheck size={14} strokeWidth={2} className="text-emerald-700" />
              30-day money-back guarantee
            </div>
          </motion.div>

          {/* Right - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            {/* Backdrop texture */}
            <div className="absolute -top-8 -right-8 w-[90%] h-[95%] hero-backdrop overflow-hidden opacity-40">
              <img
                src={BACKDROP_IMG}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            {/* Main hero image */}
            <div className="relative z-10 hero-image-arch w-full max-w-[480px] mx-auto aspect-[3/4]">
              <img
                src={HERO_IMG}
                alt="Healthy active lifestyle"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
